import { LockClosedIcon } from "@heroicons/react/24/solid";
import { GenderApiValues } from "@utils/constants";
import { getGenderValue, setGenderValue } from "@utils/helpers";
import { useEffect, useMemo } from "react";

import { Label, Paragraph, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiProviderMetadataInvitationStatusChoices,
  ProviderRoleEnums,
  ProviderType,
  useUpdateProviderMutation,
} from "@api/graphql/types-and-hooks";
import { ContentRow } from "@components/ContentRow/ContentRow";
import { FormContainer } from "@components/forms/FormLayout";
import SubmitButton from "@components/forms/FormSubmitButton/FormSubmitButton";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { mapProviderToMemberProvider } from "@pages/Members/utils/providerMapping";
import { getRoleDescription, getRoleLabelFromEnum } from "@pages/Members/utils/providerRoles";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProvidersData } from "@providers/ProvidersDataProvider";

const ASSIGNABLE_ROLES = Object.values(ProviderRoleEnums).filter((role) => role !== ProviderRoleEnums.Intake);

const GENDER_ITEMS: SelectItem[] = Object.values(GenderApiValues).map((value) => ({
  primary: getGenderValue(value) || value,
}));

interface ProviderDetailsFormData {
  firstName: string;
  lastName: string;
  gender: string;
  specifyGender: string;
  email: string;
  roles: ProviderRoleEnums[];
  learners: string[];
}

const EMPTY_FORM: ProviderDetailsFormData = {
  firstName: "",
  lastName: "",
  gender: "",
  specifyGender: "",
  email: "",
  roles: [],
  learners: [],
};

export const ProviderDetailsTab: React.FC = () => {
  const { providerDetailsQuery } = useProvidersData();
  const { data, isFetching } = providerDetailsQuery;
  const { isPassageHealthUser } = useAdminData();
  const provider = useMemo(
    () => mapProviderToMemberProvider(data?.providerById as ProviderType | undefined),
    [data?.providerById],
  );

  const updateProvider = useUpdateProviderMutation({
    onSuccess: (res) => {
      if (res.updateProvider?.status) {
        notifySuccess(res.updateProvider?.message || "Provider details updated successfully.");
        providerDetailsQuery.refetch();
      } else {
        notifyError(res.updateProvider?.message || "Failed to update provider.");
      }
    },
    onError: () => {
      notifyError("Failed to update provider. Please try again.");
    },
  });

  const { formState, onSubmit, reset, watch, RegisteredFormInput, RegisteredFormSelected, RegisteredCheckboxList } =
    useFormUtils<ProviderDetailsFormData>({
      useNativeValidation: false,
      defaultValues: EMPTY_FORM,
      mutationFn: async (values) => {
        if (!provider) return;
        await updateProvider.mutateAsync({
          providerId: provider.id,
          provider: {
            firstName: values.firstName,
            lastName: values.lastName,
            gender: setGenderValue(values.gender) ?? undefined,
            specifyGender: values.specifyGender || undefined,
            providerRole: values.roles,
            learners: values.learners,
          },
        });
      },
    });

  useEffect(() => {
    if (provider) {
      const learnersData = data?.providerById?.learnerproviderSet.edges ?? [];
      reset({
        firstName: provider.firstName,
        lastName: provider.lastName,
        gender: provider.gender ?? "",
        specifyGender: provider.specifyGender ?? "",
        email: provider.email,
        roles: provider.roleEnums ?? [],
        learners: learnersData.map((edge) => edge?.node?.learner.id).filter((id): id is string => !!id),
      });
    }
  }, [provider, data?.providerById?.learnerproviderSet.edges, reset]);

  const genderWatch = watch("gender");

  if (!provider) {
    if (isFetching) return null;
    return (
      <div className="flex w-full flex-col items-start">
        <Paragraph colorType="secondary">Provider not found.</Paragraph>
      </div>
    );
  }

  const canEditRoles = provider.portalStatus === ApiProviderMetadataInvitationStatusChoices.Accepted;

  return (
    <FormContainer onSubmit={onSubmit} className="flex w-full flex-col items-start gap-8">
      <ContentRow title="Provider Information" subtitle="Name and email for this provider.">
        <div className="flex flex-col gap-4">
          <div className="gap-x-4 lg:grid lg:grid-cols-2">
            <RegisteredFormInput
              formKey="firstName"
              formState={formState}
              label="First Name"
              disabled={isPassageHealthUser}
            />
            <RegisteredFormInput
              formKey="lastName"
              formState={formState}
              label="Last Name"
              disabled={isPassageHealthUser}
            />
          </div>
          <div className="gap-x-4 lg:grid lg:grid-cols-2">
            <RegisteredFormSelected
              formKey="gender"
              formState={formState}
              title="Gender"
              placeholderText=""
              required={false}
              items={GENDER_ITEMS}
              disabled={isPassageHealthUser}
            />
            {genderWatch === "Gender identity is not listed" && (
              <RegisteredFormInput
                formKey="specifyGender"
                formState={formState}
                label="Specify Gender"
                required={false}
                disabled={isPassageHealthUser}
              />
            )}
          </div>
          <div>
            <RegisteredFormInput formKey="email" formState={formState} label="Email" readOnly />
          </div>
        </div>
      </ContentRow>

      <ContentRow title="Role" subtitle="Select the roles this provider should have in the portal.">
        <RegisteredCheckboxList
          formState={formState}
          formKey="roles"
          title=""
          orientation="vertical"
          disabled={!canEditRoles}
          required={canEditRoles}
          // no defaultChecked: a render-time getValues() read never reflects reset()
          items={ASSIGNABLE_ROLES.map((role) => ({
            id: role,
            title: getRoleLabelFromEnum(role),
            tooltipText: getRoleDescription(role),
          }))}
        />
        {!canEditRoles && (
          <div className="mt-3 flex flex-col gap-3">
            <div className="rounded-sm bg-orange-50 p-2">
              <Paragraph displayType="normal" className="flex flex-row items-center" colorType="primary">
                <>
                  <LockClosedIcon className="h-6 w-6 text-globemallow-500 p-1 inline mr-2" />
                  <Paragraph
                    displayType="normal"
                    size="small"
                    className="flex flex-col justify-start"
                    colorType="primary"
                  >
                    <Label displayType="loud" size="small" colorType="primary">
                      Sorry, you can’t edit roles yet
                    </Label>
                    {provider.portalStatus === null
                      ? "Set this provider's roles when you send their invite from the top of this page."
                      : "This user’s invitation is still pending. You’ll be able to update this user’s role once they’ve accepted their invitation."}
                  </Paragraph>
                </>
              </Paragraph>
            </div>
          </div>
        )}
      </ContentRow>

      <div className="flex w-full justify-end">
        <SubmitButton isLoading={formState.isSubmitting} buttonText="Update" />
      </div>
    </FormContainer>
  );
};
